import { useIO, useLinked } from "@type-r/react";
import { Column, Columns, FormSelect, Input, Option, asCurrency, div } from "lqx-react";
import React from 'react';
import { PolicyBuyerSchedule } from "../../../api/policy";
import { ErrorHighligter, InputWithMask, YesNoSelect } from "../../../shared";
import { BuyersLabel } from "./shared";

export const PolicyBuyerForm = ({ buyer, $isBuyerValid }: { buyer: PolicyBuyerSchedule, $isBuyerValid }) => {
    const $validationRequests = useLinked(0);

    useIO(async () => {
        const isValid = await buyer.validateBuyer();
        $isBuyerValid.set(isValid);
    }, [$validationRequests.value]);

    const validate = () => $validationRequests.set($validationRequests.value + 1);

    return (
        <>
            <PolicyBuyerEditFormHeader>
                <Columns>
                    <Column>
                        <PolicyBuyerEditFormHeaderName>{buyer.buyerName}</PolicyBuyerEditFormHeaderName>
                        <PolicyBuyerEditFormHeaderAddress>
                            {buyer.obligor.address.line1}
                            {buyer.obligor.address.countryCode}
                        </PolicyBuyerEditFormHeaderAddress>
                    </Column>
                    <Column>
                        <PolicyBuyerEditFormHeaderDuns>Duns #{buyer.buyerDUNS}</PolicyBuyerEditFormHeaderDuns>
                    </Column>
                </Columns>
            </PolicyBuyerEditFormHeader>
            <Columns>
                <Column><BuyersLabel>Maximum Credit Limit ($) *</BuyersLabel></Column>
                <Column><BuyersLabel>State *</BuyersLabel></Column>
                <Column><BuyersLabel>Estimated Longest Payment Term</BuyersLabel></Column>
            </Columns>
            <Columns>
                <Column>
                    <ErrorHighligter error={buyer.errors['maxCreditLimit']}>
                        <InputWithMask number onBlur={validate} $value={buyer.$.maxCreditLimit} mask={asCurrency(buyer.$.maxCreditLimit)} />
                    </ErrorHighligter>
                </Column>
                <Column>
                    <ErrorHighligter error={buyer.errors['state']}>
                        <FormSelect {...{ onBlur: validate }} $value={buyer.$.state} >
                            <Option value="Bound">Bound</Option>
                            <Option value="2">Bound</Option>
                        </FormSelect>
                    </ErrorHighligter>
                </Column>
                <Column>
                    <ErrorHighligter error={buyer.errors['maxPaymentTerms']}>
                        <Input onBlur={validate} number $value={buyer.$.maxPaymentTerms} />
                    </ErrorHighligter>
                </Column>
            </Columns>
            <Columns>
                <Column><BuyersLabel>Indemnity (%) *</BuyersLabel></Column>
                <Column><BuyersLabel>Premium Rate (%) *</BuyersLabel></Column>
                <Column><BuyersLabel>Estimated Utilization ($)</BuyersLabel></Column>
            </Columns>
            <Columns>
                <Column>
                    <ErrorHighligter error={buyer.errors['indemnity']}>
                        <InputWithMask number onBlur={validate} $value={buyer.$.indemnity} mask={buyer.indemnity.toFixed(0) + '%'} />
                    </ErrorHighligter>
                </Column>
                <Column>
                    <ErrorHighligter error={buyer.errors['premiumRate']}>
                        <InputWithMask number onBlur={validate} $value={buyer.$.premiumRate} mask={buyer.premiumRate.toFixed(2) + '%'} />
                    </ErrorHighligter>
                </Column>
                <Column>
                    <ErrorHighligter error={buyer.errors['estUtilization']}>
                        <InputWithMask number onBlur={validate} $value={buyer.$.estUtilization} mask={asCurrency(buyer.estUtilization)} />
                    </ErrorHighligter>
                </Column>
            </Columns>
            <Columns>
                <Column><BuyersLabel>Attachment Period Start Date *</BuyersLabel></Column>
                <Column><BuyersLabel>Attachment Period (days)</BuyersLabel></Column>
                <Column><BuyersLabel>Seller</BuyersLabel></Column>
            </Columns>
            <Columns>
                <Column>
                    <ErrorHighligter error={buyer.errors['attachmentPeriodStartDate']}>
                        <Input {...{ onCalendarClose: validate }} date $value={buyer.$.attachmentPeriodStartDate} />
                    </ErrorHighligter>
                </Column>
                <Column>
                    <ErrorHighligter error={buyer.errors['attachmentPeriod']}>
                        <Input onBlur={validate} $value={buyer.$.attachmentPeriod} />
                    </ErrorHighligter>
                </Column>
                <Column>
                    <ErrorHighligter error={buyer.errors['seller']}>
                        <Input onBlur={validate} $value={buyer.$.seller} />
                    </ErrorHighligter>
                </Column>
            </Columns>
            <Columns>
                <Column><BuyersLabel>Description of Goods</BuyersLabel></Column>
                <Column><BuyersLabel>Minimum Premium ($)</BuyersLabel></Column>
                <Column><BuyersLabel>Deposit Premium ($)</BuyersLabel></Column>
            </Columns>
            <Columns>
                <Column>
                    <ErrorHighligter error={buyer.errors['descriptionOfGoods']}>
                        <Input onBlur={validate} $value={buyer.$.descriptionOfGoods} />
                    </ErrorHighligter>
                </Column>
                <Column>
                    <ErrorHighligter error={buyer.errors['minimumPremium']}>
                        <InputWithMask number onBlur={validate} $value={buyer.$.minimumPremium} mask={asCurrency(buyer.minimumPremium)} />
                    </ErrorHighligter>
                </Column>
                <Column>
                    <ErrorHighligter error={buyer.errors['depositPremium']}>
                        <InputWithMask number onBlur={validate} $value={buyer.$.depositPremium} mask={asCurrency(buyer.depositPremium)} />
                    </ErrorHighligter>
                </Column>
            </Columns>
            <Columns>
                <Column><BuyersLabel>Is ther past due history?</BuyersLabel></Column>
                <Column><BuyersLabel>If yes, describe past due history</BuyersLabel></Column>
                <Column><BuyersLabel>Limit Currency *</BuyersLabel></Column>
            </Columns>
            <Columns>
                <Column>
                    <ErrorHighligter error={buyer.errors['hasPastDueHistory']}>
                        <YesNoSelect {...{ onBlur: validate }} $value={buyer.$.hasPastDueHistory} />
                    </ErrorHighligter>
                </Column>
                <Column>
                    <ErrorHighligter error={buyer.errors['pastDueDescription']}>
                        <Input onBlur={validate} $value={buyer.$.pastDueDescription} />
                    </ErrorHighligter>
                </Column>
                <Column>
                    <ErrorHighligter error={buyer.errors['limitCurrency']}>
                        <FormSelect {...{ onBlur: validate }} $value={buyer.$.limitCurrency} >
                            <Option value="AUD">AUD</Option>
                            <Option value="CAD">CAD</Option>
                            <Option value="CHF">CHF</Option>
                            <Option value="EUR">EUR</Option>
                            <Option value="GBP">GBP</Option>
                            <Option value="SGD">SGD</Option>
                            <Option value="USD">USD</Option>
                        </FormSelect>
                    </ErrorHighligter>
                </Column>
            </Columns>
            <Columns>
                <Column><BuyersLabel>Special Conditions?</BuyersLabel></Column>
                <Column><BuyersLabel>If yes, describe special conditions</BuyersLabel></Column>
                <Column><BuyersLabel>Cancellable? *</BuyersLabel></Column>
            </Columns>
            <Columns>
                <Column>
                    <ErrorHighligter error={buyer.errors['buyerSpecialConditions']}>
                        <YesNoSelect {...{ onBlur: validate }} $value={buyer.$.buyerSpecialConditions} />
                    </ErrorHighligter>
                </Column>
                <Column>
                    <ErrorHighligter error={buyer.errors['specialConditionDescription']}>
                        <Input onBlur={validate} $value={buyer.$.specialConditionDescription} />
                    </ErrorHighligter>
                </Column>
                <Column>
                    <ErrorHighligter error={buyer.errors['isCancellable']}>
                        <YesNoSelect {...{ onBlur: validate }} $value={buyer.$.isCancellable} />
                    </ErrorHighligter>
                </Column>
            </Columns>
        </>
    )
};

const PolicyBuyerEditFormHeader = div({
    backgroundColor: '#f7fcff',
    marginBottom: '10px'
});

const PolicyBuyerEditFormHeaderName = div({
    fontWeight: 800,
    fontSize: '12px',
    paddingTop: '10px',
    paddingLeft: '10px'
});

const PolicyBuyerEditFormHeaderDuns = div({
    color: 'gray',
    fontSize: '12px',
    paddingTop: '10px',
});

const PolicyBuyerEditFormHeaderAddress = div({
    padding: '10px',
    fontWeight: 500,
    fontSize: '10px'
});
